import { makeStyles } from '@mui/styles';
import Divider from 'apollo-react/components/Divider';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Popover from 'apollo-react/components/Popover';
import TextField from 'apollo-react/components/TextField';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import { ArrowDown, Check, Close, Search } from 'apollo-react-icons';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { getHighlightedText } from './Sidenav/customMenus/utils';
import { ReactComponent as PushPinIcon } from '../assets/icons/pushpin.svg';
import { DEBOUNCE_SEARCH_TIME } from '../constants/Common';
import { INPUT_ICON_DEFAULT_SIZE } from '../constants/CommonStylesConstants';
import { usePrevious } from '../custom-hooks';
import { useTranslation } from '../custom-hooks/useTranslation';
import { useWindowDimensions } from '../custom-hooks/useWindowDimensions';
import { getUITA } from '../utils/commonUtils';
import {
  getFullHistoryItemsData,
  getSearchHistoryByStoreKey,
  saveSearchHistoryByStoreKey,
  updatePinnedItems,
  updateRecentItems,
} from '../utils/enhancedSearchDropdownUtils';

const MIN_SELECT_WIDTH = 250;

const useStyles = makeStyles({
  group: {
    fontWeight: 'bold',
    color: '#000000',
  },
  item: {
    textIndent: 12,
    paddingRight: 25,
  },
  menuItem: {
    opacity: '1 !important',
    paddingLeft: 25,
    paddingRight: 32,
  },
  searchInput: {
    cursor: 'pointer',
  },
  searchTextInput: {
    flex: 1,
    margin: '10px 10px 5px 10px',
  },
  emptyMessage: {
    textAlign: 'center',
    margin: '25px 0px',
  },
  icon: INPUT_ICON_DEFAULT_SIZE,
  rotateIcon: {
    transform: 'rotate(180deg)',
  },
  divider: {
    marginLeft: 10,
    marginRight: 10,
  },
  selectedMenuItem: {
    border: '2px solid black',
  },
  checkIcon: {
    padding: 2,
    width: 16,
    height: 16,
    boxSizing: 'content-box',
    position: 'absolute',
    left: 8,
  },
  pushPinIcon: {
    padding: 2,
    width: 16,
    height: 16,
    boxSizing: 'content-box',
  },
  showPinIcon: {
    '&:hover $pushPinIconButton': {
      display: 'block !important',
    },
  },
  pushPinIconButton: {
    position: 'absolute',
    right: '8px !important',
    display: ({ isPinned }) => (isPinned ? 'block' : 'none'),
  },
  menuItemsContainer: {
    maxHeight: 350,
    overflowY: 'auto',
    paddingBottom: 5,
  },
  dropdown: {
    '& button': {
      '&:hover': {
        backgroundColor: 'transparent !important',
        color: '#4d4d4d !important',
      },
    },
  },
  hiddenText: {
    visibility: 'hidden',
    position: 'absolute',
    whiteSpace: 'pre',
    height: 0,
  },
  highlightedText: {
    fontWeight: 'bold',
  },
});

const MenuRow = ({
  searchItem,
  isSelected,
  searchText,
  pinnedItems,
  handlePin,
  handleSelect,
  isPinEnabled,
  isRecentItem,
  isPinItem,
}) => {
  const isPinned = pinnedItems.includes(searchItem.searchName);

  const classes = useStyles({ isPinned });
  const translateString = useTranslation();

  const isGroupHeader = searchItem.group;

  const showPinIcon = isPinEnabled && !isGroupHeader;
  const label = searchItem.translatedSearchName;

  const handlePinClick = (e) => {
    e.stopPropagation();
    handlePin(!isPinned, searchItem.searchName);
  };

  const pinnedText = isPinned ? 'Unpin' : 'Pin';

  return (
    <MenuItem
      className={classNames(classes.menuItem, {
        [classes.selectedMenuItem]: isSelected && !isPinItem && !isRecentItem,
        [classes.showPinIcon]: showPinIcon,
      })}
      key={searchItem.id}
      disabled={searchItem.group}
      onClick={() => handleSelect(searchItem.searchName)}
    >
      {isSelected && <Check className={classes.checkIcon} />}
      <span
        className={isGroupHeader ? classes.group : classes.item}
        ui-ta={getUITA('SearchNameSelect', 'SearchNameOption', label)}
      >
        {isGroupHeader || !searchText
          ? label
          : getHighlightedText(label, searchText, classes.highlightedText)}
      </span>
      {showPinIcon && (
        <Tooltip title={translateString(pinnedText)} placement="right">
          <IconButton
            className={classes.pushPinIconButton}
            size="small"
            onClick={handlePinClick}
            ui-ta={getUITA(`${pinnedText} action`)}
          >
            <PushPinIcon className={classes.pushPinIcon} />
          </IconButton>
        </Tooltip>
      )}
    </MenuItem>
  );
};

const SearchInput = ({ handleSearch, clearSearch }) => {
  const classes = useStyles();
  const translateString = useTranslation();

  const searchInputRef = useRef();

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    searchInputRef.current.focus();

    return () => {
      setInputValue('');
      clearSearch();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = ({ target: { value } }) => {
    const newSearchText = value.length === 1 ? value.trim() : value;

    setInputValue(newSearchText);
    handleSearch(newSearchText);
  };

  const clearSearchText = () => {
    setInputValue('');
    searchInputRef.current.focus();

    clearSearch();
  };

  return (
    <div style={{ display: 'flex' }}>
      <TextField
        className={classes.searchTextInput}
        startIcon={<Search className={classes.icon} />}
        placeholder={translateString('Search')}
        icon={inputValue !== '' ? <Close className={classes.icon} /> : null}
        iconProps={{ onClick: clearSearchText }}
        value={inputValue ?? ''}
        inputRef={searchInputRef}
        onChange={handleChange}
        ui-ta={getUITA('SearchNameSelect', 'SearchInput')}
      />
    </div>
  );
};

const EnhancedSearchDropdown = ({
  storeKey,
  selectOptions = [],
  onChange,
  selectedValue,
  fullWidth = false,
  showRecent = false,
  showPinned = false,
}) => {
  const { isMobileDevice } = useWindowDimensions();
  const classes = useStyles();
  const translateString = useTranslation();

  const anchorElRef = useRef(null);
  const hiddenTextRef = useRef(null);

  const [isListOpen, setIsListOpen] = useState(false);
  const [optionsToRender, setOptionToRender] = useState(selectOptions);
  const [searchText, setSearchText] = useState('');
  const [inputWidth, setInputWidth] = useState(MIN_SELECT_WIDTH);
  const [pinnedItems, setPinnedItems] = useState([]);
  const [recentItems, setRecentItems] = useState([]);

  const prevIsListOpened = usePrevious(isListOpen);

  const { translatedSearchName } =
    selectOptions.find(({ searchName }) => selectedValue === searchName) || {};

  useEffect(() => {
    setOptionToRender(selectOptions);
  }, [selectOptions]);

  //when dropdown is opened, retrieve search history from local storage
  //when dropdown is closed we need to store search history in local storage
  useEffect(() => {
    if (prevIsListOpened && !isListOpen) {
      saveSearchHistoryByStoreKey(storeKey, recentItems, pinnedItems);
    }

    if (isListOpen) {
      const { recent, pinned } = getSearchHistoryByStoreKey(storeKey, selectOptions);

      setRecentItems(recent);
      setPinnedItems(pinned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListOpen]);

  useEffect(() => {
    //calculate the width of select field
    if (hiddenTextRef.current && !fullWidth) {
      const textWidth = hiddenTextRef.current.offsetWidth + 80;
      const inputWidth = textWidth > MIN_SELECT_WIDTH ? textWidth : MIN_SELECT_WIDTH;
      setInputWidth(`${inputWidth}px`);
    }
  }, [selectedValue, fullWidth]);

  useEffect(() => {
    if (searchText) {
      const filteredOptions = selectOptions.filter(
        ({ group, translatedSearchName = '' }) =>
          group || translatedSearchName.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
      );
      const filteredWithoutEmptyGroups = filteredOptions.filter(
        ({ group }, index) =>
          !(group && (filteredOptions[index + 1]?.group || index === filteredOptions.length - 1))
      );

      setOptionToRender(filteredWithoutEmptyGroups);
      return;
    }

    if (selectOptions.length) {
      setOptionToRender(selectOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleSelect = (value) => {
    if (value !== selectedValue) {
      setIsListOpen(false);
      onChange(value);

      const updatedRecentItems = updateRecentItems(recentItems, value);
      setRecentItems(updatedRecentItems);
    }
  };

  const debouncedHandleSearch = useMemo(
    () => _.debounce((value) => setSearchText(value), DEBOUNCE_SEARCH_TIME),
    []
  );

  const handlePinClick = (shouldPinItem, searchName) => {
    const newPinnedItems = updatePinnedItems(pinnedItems, searchName, shouldPinItem);

    setPinnedItems(newPinnedItems);
  };

  const recentItemsToRender = useMemo(
    () =>
      showRecent && !searchText && recentItems.length
        ? [
            { id: 'recent', group: true, translatedSearchName: translateString('Recent Items') },
            ...getFullHistoryItemsData(selectOptions, recentItems, 'RECENT'),
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentItems, selectOptions, searchText]
  );

  const pinnedItemsToRender = useMemo(
    () =>
      showPinned && !searchText && pinnedItems.length
        ? [
            { id: 'pinned', group: true, translatedSearchName: translateString('Pinned Items') },
            ...getFullHistoryItemsData(selectOptions, pinnedItems, 'PINNED'),
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pinnedItems, selectOptions, searchText]
  );

  const selectWidth = isMobileDevice || fullWidth ? '100%' : inputWidth;
  const dropdownMinWidth = isMobileDevice
    ? '95%'
    : fullWidth
    ? anchorElRef?.current?.offsetWidth > MIN_SELECT_WIDTH
      ? anchorElRef?.current?.offsetWidth
      : MIN_SELECT_WIDTH
    : '400px';

  return (
    <>
      {/* Hidden span to measure the text width */}
      <span ref={hiddenTextRef} className={classes.hiddenText}>
        {translatedSearchName || ''}
      </span>
      <TextField
        className={classes.dropdown}
        icon={<ArrowDown className={isListOpen ? classes.rotateIcon : null} />}
        onClick={() => setIsListOpen(!isListOpen)}
        ref={anchorElRef}
        value={translatedSearchName}
        fullWidth={isMobileDevice || fullWidth}
        inputProps={{
          readOnly: true,
          className: classes.searchInput,
          style: { width: selectWidth },
        }}
        ui-ta={getUITA('SearchNameSelect')}
      />
      <Popover
        onClose={() => setIsListOpen(!isListOpen)}
        open={isListOpen}
        anchorEl={anchorElRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobileDevice ? 'center' : 'left' }}
        slotProps={{
          paper: {
            style: { minWidth: dropdownMinWidth },
          },
        }}
      >
        <>
          {!!selectOptions.length && (
            <SearchInput
              handleSearch={debouncedHandleSearch}
              clearSearch={() => setSearchText('')}
            />
          )}
          <div className={classes.menuItemsContainer}>
            {optionsToRender.length ? (
              <>
                {pinnedItemsToRender.map((search, index) => (
                  <>
                    <MenuRow
                      searchItem={search}
                      isSelected={search.searchName === selectedValue}
                      key={search.id}
                      searchText={searchText}
                      handleSelect={handleSelect}
                      pinnedItems={pinnedItems}
                      handlePin={handlePinClick}
                      isPinEnabled={showPinned}
                      isPinItem
                    />
                    {pinnedItemsToRender.length - 1 === index && (
                      <Divider className={classes.divider} />
                    )}
                  </>
                ))}
                {recentItemsToRender.map((search, index) => (
                  <>
                    <MenuRow
                      searchItem={search}
                      isSelected={search.searchName === selectedValue}
                      key={search.id}
                      searchText={searchText}
                      handleSelect={handleSelect}
                      pinnedItems={pinnedItems}
                      handlePin={handlePinClick}
                      isPinEnabled={showPinned}
                      isRecentItem
                    />
                    {recentItemsToRender.length - 1 === index && (
                      <Divider className={classes.divider} />
                    )}
                  </>
                ))}
                {optionsToRender.map((search) => (
                  <MenuRow
                    searchItem={search}
                    isSelected={search.searchName === selectedValue}
                    key={search.id}
                    searchText={searchText}
                    handleSelect={handleSelect}
                    pinnedItems={pinnedItems}
                    handlePin={handlePinClick}
                    isPinEnabled={showPinned}
                  />
                ))}
              </>
            ) : (
              <Typography className={classes.emptyMessage}>
                {translateString('No data to display')}
              </Typography>
            )}
          </div>
        </>
      </Popover>
    </>
  );
};

export default EnhancedSearchDropdown;
